import { observer } from "mobx-react";
import { useUserStore } from "@/stores/RootStoreContext";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { CloseButton } from "@/components-v2/Button";
import { useLanguage, useRootStore } from "@/stores/RootStoreContext";
import {
  AggregatedContactDetailsTab,
  prepAggregatedContactDetailsPage,
  prepEditAggregatedDetailsDialog,
} from "./aggregatedContactDetailsDomain";
import { Box } from "@/components-v2/Box";
import { PortfolioType, usePortfolio } from "@/portfolio/portfolioDomain";
import { useEffect } from "react";
import {
  AggregatedContactDetailsBody,
  AggregatedContactDetailsHeader,
  AggregatedContactDetailsEditDialog,
} from "./contactsComponents";
import { initContactsPage, resetDetailsSearch } from "./contactsDomain";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator.tsx";
import { isPending } from "@/common/common.ts";

export const AggregatedContactDetailsPage = observer(
  ({
    portfolioType,
    id,
    tab,
    portfolioId,
  }: {
    portfolioType: PortfolioType;
    id: string;
    tab: AggregatedContactDetailsTab;
    portfolioId?: string;
  }) => {
    const { dispatch, contacts, messageStore } = useRootStore();
    const userStore = useUserStore();
    const language = useLanguage();
    const { portfolio } = usePortfolio({
      activeHeader: "contacts",
      portfolioId,
      portfolioType,
    });
    const props = prepAggregatedContactDetailsPage({
      dispatch,
      portfolio,
      contacts,
      id,
      language,
      tab,
      access: { companies: userStore.companies, userRoles: userStore.userRoles },
    });

    useEffect(() => {
      if (!contacts) {
        dispatch(initContactsPage(portfolio));
      }
    }, [dispatch, contacts, portfolio]);

    useEffect(() => {
      dispatch([resetDetailsSearch()]);
    }, [dispatch, id, tab]);

    return (
      <>
        <FullScreenLayout
          alertStore={messageStore}
          appBar={
            <Box bg padding>
              <CloseButton onClick={props.navigateBack} />
            </Box>
          }
          pageHeader={<AggregatedContactDetailsHeader {...props} />}
          centerPanel={
            isPending(contacts?.response) ? (
              <LoadingIndicator fullscreen />
            ) : (
              <AggregatedContactDetailsBody {...props} />
            )
          }
        />
        {contacts?.editDetailsFieldDialogState && (
          <AggregatedContactDetailsEditDialog
            {...prepEditAggregatedDetailsDialog(
              contacts.editDetailsFieldDialogState,
              dispatch,
              portfolio,
            )}
          />
        )}
      </>
    );
  },
);
